import React from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { Routes } from "../..";
import { AlertMessageLink, PiggyBankCircleIcon } from "../../../components";
import {
  AlertMessageConditions,
  AlertMessageHierarchyKeys,
  AlertMessageType,
} from "./types";

/**
 * @constant
 * Object map with the intended location as a key and a ranked list of alert message types ordered from most import to least important.
 * Only the lowest index, active === true message will be displayed for the given key/location.
 */
export const alertMessageHierarchy: Record<
  AlertMessageHierarchyKeys,
  AlertMessageConditions[]
> = {
  [AlertMessageHierarchyKeys.SUMMARY_PAGE__PHARMACY_SECTION]: [
    AlertMessageConditions.NO_SAVED_PHARMACIES,
    AlertMessageConditions.ONE_SAVED_PHARMACY_STALE_ACCOUNT,
    AlertMessageConditions.ONE_SAVED_PHARMACY,
    AlertMessageConditions.STALE_ACCOUNT_PHARMACIES,
    AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES,
    AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES,
  ],
  [AlertMessageHierarchyKeys.SUMMARY_PAGE__DRUG_SECTION]: [
    AlertMessageConditions.NO_SAVED_DRUGS,
    AlertMessageConditions.STALE_ACCOUNT_DRUGS,
  ],
  [AlertMessageHierarchyKeys.PLAN_DETAILS__PHARMACY_SECTION]: [
    AlertMessageConditions.NO_SAVED_PHARMACIES,
    AlertMessageConditions.NO_SAVED_DRUGS,
    AlertMessageConditions.ONE_SAVED_PHARMACY,
    AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES,
    AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES,
  ],
  [AlertMessageHierarchyKeys.PLAN_COMPARISON_TABLE]: [
    AlertMessageConditions.NO_SAVED_PHARMACIES,
    AlertMessageConditions.NO_SAVED_DRUGS,
    AlertMessageConditions.ONE_SAVED_PHARMACY,
  ],
  [AlertMessageHierarchyKeys.PLAN_COMPARISON_NETWORK_STATUS]: [
    AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES,
    AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES,
  ],
  [AlertMessageHierarchyKeys.IN_NETWORK_PHARMACY_FINDER]: [
    AlertMessageConditions.NO_SAVED_DRUGS,
  ],
};

export const defaultAlertMessageState: Record<
  AlertMessageConditions,
  AlertMessageType
> = {
  [AlertMessageConditions.NO_SAVED_PHARMACIES]: {
    action: undefined,
    active: false,
    body: (_, pageContext) => (
      <FormattedMessage
        id="lcp.messages.no_saved_pharmacies.body"
        values={{
          addPharmacies: (
            <AlertMessageLink
              to={{
                pathname: Routes.inNetworkPharmacy.replace(
                  ":id",
                  pageContext?.planId || ""
                ),
                state: pageContext?.routerState,
              }}
              condition={AlertMessageConditions.NO_SAVED_PHARMACIES}
              textKey="lcp.messages.no_saved_pharmacies.body.link.add_pharmacies"
            />
          ),
        }}
      />
    ),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.NO_SAVED_PHARMACIES,
    title: undefined,
  },
  [AlertMessageConditions.NO_SAVED_DRUGS]: {
    action: undefined,
    active: false,
    body: (_, pageContext) => (
      <FormattedMessage
        id="lcp.messages.no_saved_drugs.body"
        values={{
          addYourPrescriptionDrugs: (
            <AlertMessageLink
              to={{
                pathname: Routes.managePrescriptions,
                state: pageContext?.routerState,
              }}
              condition={AlertMessageConditions.NO_SAVED_DRUGS}
              textKey="lcp.messages.no_saved_drugs.body.links.add_your_prescription_drugs"
            />
          ),
        }}
      />
    ),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.NO_SAVED_DRUGS,
    title: undefined,
  },
  [AlertMessageConditions.ONE_SAVED_PHARMACY_STALE_ACCOUNT]: {
    action: undefined,
    active: false,
    body: () => (
      <FormattedMessage
        id="lcp.messages.one_saved_pharmacy.stale_account.body"
        values={{
          addPharmacies: (
            <AlertMessageLink
              to={Routes.summary.managePharmacies}
              condition={
                AlertMessageConditions.ONE_SAVED_PHARMACY_STALE_ACCOUNT
              }
              textKey="lcp.messages.one_saved_pharmacy.stale_account.body.links.add_pharmacies"
            />
          ),
        }}
      />
    ),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.ONE_SAVED_PHARMACY_STALE_ACCOUNT,
    title: t => t("lcp.messages.one_saved_pharmacy.stale_account.title"),
  },
  [AlertMessageConditions.ONE_SAVED_PHARMACY]: {
    active: false,
    action: undefined,
    body: () => (
      <FormattedMessage
        id="lcp.messages.one_saved_pharmacy_with_link.body"
        values={{
          morePharmacies: (
            <AlertMessageLink
              to={Routes.managePharmacies}
              condition={AlertMessageConditions.ONE_SAVED_PHARMACY}
              textKey="lcp.messages.one_saved_pharmacy.body.link.more_pharmacies"
            />
          ),
        }}
      />
    ),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.ONE_SAVED_PHARMACY,
    title: t => t("lcp.messages.one_saved_pharmacy.title"),
  },
  [AlertMessageConditions.STALE_ACCOUNT_PHARMACIES]: {
    action: undefined,
    active: false,
    body: t => t("summary_page.pharmacies.alert.no_pharmacies"),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.STALE_ACCOUNT_PHARMACIES,
    title: undefined,
  },
  [AlertMessageConditions.STALE_ACCOUNT_DRUGS]: {
    action: undefined,
    active: false,
    body: t => t("summary_page.drugs.alert.no_drugs"),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.STALE_ACCOUNT_DRUGS,
    title: undefined,
  },
  [AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES]: {
    action: (_, pageContext) => (
      <AlertMessageLink
        to={{
          pathname: Routes.inNetworkPharmacy.replace(
            ":id",
            pageContext?.planId || ""
          ),
          state: pageContext?.routerState,
        }}
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        condition={AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES}
        textKey="lcp.messages.no_in_network_pharmacies.action"
        treatAsButton
      />
    ),
    active: false,
    body: t => t("lcp.messages.no_in_network_pharmacies.body"),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.NO_IN_NETWORK_SAVED_PHARMACIES,
    title: t => t("lcp.messages.no_in_network_pharmacies.title"),
  },
  [AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES]: {
    action: (_, pageContext) => (
      <AlertMessageLink
        to={{
          pathname: Routes.inNetworkPharmacy.replace(
            ":id",
            pageContext?.planId || ""
          ),
          state: pageContext?.routerState,
        }}
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        condition={AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES}
        textKey="lcp.messages.no_preferred_pharmacies.action"
        treatAsButton
      />
    ),
    active: false,
    body: t => t("lcp.messages.no_preferred_pharmacies.body"),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES,
    title: t => t("lcp.messages.no_preferred_pharmacies.title"),
  },
};
